import Link from '@/components/Button'
import ContactForm from '@/components/ContactForm'
import FlexibleContent from '@/components/FlexibleContent'
import Icon from '@/components/Icon'
import ImageComparisonSlider from '@/components/ImageComparisonSlider'
import Main from '@/components/Main'
import SEO from '@/components/SEO'
import { faArrowRight } from '@fortawesome/sharp-solid-svg-icons'
import Image from 'next/image'
import styles from '@/styles/pages/_Casestudy.module.scss'

export default function SingleCaseStudy(props) {
  if (props.loading) {
    return null
  }

  const caseStudy = props?.data?.caseStudy
  if (!caseStudy) {
    return null
  }

  const { caseStudyFields } = caseStudy
  const caseStudyTitle = caseStudy.title
  const metaDescription = caseStudy?.seo?.metaDesc
  const form = props?.data?.contactForm
  const flexibleContent = caseStudy?.flexibleContent?.flexibleContentFields ?? []
  const seoTitle = caseStudy?.seo?.title

  return (
    <Main>
      <SEO title={seoTitle || caseStudyTitle} description={metaDescription} />

      <div className={styles.caseHeader}>
        <div className={styles.caseHeaderWrapper}>
          {caseStudyFields.heroImageVideo === 'Video' &&
            caseStudyFields.heroVideo?.node?.mediaItemUrl &&
            caseStudyFields.heroVideo?.node?.mimeType?.startsWith('video/') && (
              <video autoPlay loop playsInline muted>
                <source src={caseStudyFields.heroVideo.node.mediaItemUrl} type={caseStudyFields.heroVideo.node.mimeType} />
                Your browser does not support the video tag.
              </video>
            )}

          {caseStudyFields.heroImageVideo === 'Image' && caseStudyFields.heroImage?.node?.sourceUrl && (
            <Image
              src={caseStudyFields.heroImage.node.sourceUrl}
              alt={caseStudyFields.heroImage.node.altText || ''}
              width={caseStudyFields.heroImage.node.mediaDetails?.width || 1000}
              height={caseStudyFields.heroImage.node.mediaDetails?.height || 700}
              sizes="(max-width: 768px) 100vw, 44vw"
              style={{ width: '100%', height: 'auto' }}
              preload
              fetchPriority="high"
            />
          )}

          <div className={styles.caseHeaderContent}>
            <div dangerouslySetInnerHTML={{ __html: caseStudyFields.title }} />
            {caseStudy.terms?.nodes?.length > 0 && (
              <div className={styles.caseHeaderCategories}>
                {caseStudy.terms.nodes.map((service, index) => (
                  <a key={index} href={`/work?category=${service.slug}`} className={styles.serviceCategory}>
                    {service.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <FlexibleContent content={flexibleContent} />

      {caseStudyFields.clientDescription && (
        <div className={styles.clientInformation}>
          <div className={styles.clientInformationWrapper}>
            <div className={styles.clientMainInfo}>
              {caseStudyFields.clientLogo?.node?.sourceUrl && (
                <Image
                  src={caseStudyFields.clientLogo.node.sourceUrl}
                  width={410}
                  height={150}
                  alt={caseStudyFields.clientLogo.node.altText || ''}
                  sizes="(max-width: 768px) 100vw, 410px"
                  style={{ width: 'auto', height: 'auto' }}
                />
              )}
              <div dangerouslySetInnerHTML={{ __html: caseStudyFields.clientDescription }} />
            </div>
            <div className={styles.clientDetails}>
              <div>
                <p className={styles.clientDetailTitle}>Client</p>
                <p>{caseStudyFields.clientName}</p>
              </div>
              <div>
                <p className={styles.clientDetailTitle}>Date</p>
                <p>{caseStudyFields.clientDate}</p>
              </div>
              <div>
                <p className={styles.clientDetailTitle}>Industry</p>
                <p>{caseStudyFields.clientIndustry}</p>
              </div>
              <div>
                <p className={styles.clientDetailTitle}>Website</p>
                {caseStudyFields.clientLink && (
                  <a
                    href={caseStudyFields.clientLink.url}
                    target={caseStudyFields.clientLink.target || '_self'}
                    rel={caseStudyFields.clientLink.target === '_blank' ? 'noopener noreferrer' : undefined}
                  >
                    {caseStudyFields.clientLink.title || ''}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {caseStudyFields.problemDescription && (
        <div className={styles.problem}>
          <div className={styles.problemWrapper}>
            {caseStudyFields.problemBeforeImage?.node?.sourceUrl && caseStudyFields.problemAfterImage?.node?.sourceUrl && (
              <div className={styles.problemImage}>
                <p className={styles.problemSliderInstruction}>
                  Slide to see the transformation
                  <Icon icon={faArrowRight} style={{ marginLeft: '8px', color: '#ef4c20' }} />
                </p>
                <ImageComparisonSlider
                  image1={caseStudyFields.problemAfterImage.node.sourceUrl}
                  image2={caseStudyFields.problemBeforeImage.node.sourceUrl}
                  sliderLineColor="transparent" /* Remove default line color */
                  sliderLineWidth={0} /* Remove default line width */
                  handleColor="transparent" /* Remove default handle color */
                  handleSize={0} /* Remove default handle size */
                />
              </div>
            )}

            <div className={styles.problemInfo}>
              <Image src="/problem.svg" alt="Lightbulb icon" width={50} height={50} />
              <p className={styles.subheading}>{caseStudyFields.problemSubheading}</p>
              <h2 className={styles.heading}>{caseStudyFields.problemHeading}</h2>
              <div className={styles.problemDescription} dangerouslySetInnerHTML={{ __html: caseStudyFields.problemDescription }} />
            </div>
          </div>
        </div>
      )}
      {caseStudyFields.testimonial && (
        <div className={styles.testimonial}>
          <div className={styles.testimonialWrapper}>
            {caseStudyFields.headshotImage?.node?.sourceUrl ? (
              <Image
                src={caseStudyFields.headshotImage.node.sourceUrl}
                alt={caseStudyFields.headshotImage.node.altText || 'Testimonial headshot'}
                width={127}
                height={127}
                sizes="127px"
              />
            ) : (
              <Image src="/silhouette.svg" alt="Silhouette placeholder" width={127} height={127} />
            )}
            <div dangerouslySetInnerHTML={{ __html: caseStudyFields.testimonial }} />
          </div>
        </div>
      )}

      {caseStudyFields.solutionDescription && (
        <div className={styles.solution}>
          <div className={styles.solutionWrapper}>
            <div className={styles.solutionInfo}>
              <div className={styles.solutionInfoWrapper}>
                <Image src="/solution.svg" alt="Gear icon" width={50} height={50} />
                <p className={styles.subheading}>{caseStudyFields.solutionSubheading}</p>
                <h2 className={styles.heading}>{caseStudyFields.solutionHeading}</h2>
                <div className={styles.solutionDescription} dangerouslySetInnerHTML={{ __html: caseStudyFields.solutionDescription }} />
                {caseStudyFields.solutionLink?.url && (
                  <Link
                    href={caseStudyFields.solutionLink.url}
                    className={styles.solutionLink}
                    target={caseStudyFields.solutionLink.target}
                  >
                    {caseStudyFields.solutionLink.title}
                    <Icon icon={faArrowRight} style={{ marginLeft: '8px' }} />
                  </Link>
                )}
              </div>
            </div>

            <div className={styles.solutionImage}>
              {caseStudyFields.solutionImage?.node?.sourceUrl && (
                <Image
                  src={caseStudyFields.solutionImage.node.sourceUrl}
                  alt={caseStudyFields.solutionImage.node.altText || ''}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  style={{ objectFit: 'cover' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {caseStudyFields.conclusionDescription && (
        <div className={styles.conclusion}>
          <div className={styles.conclusionWrapper}>
            <div dangerouslySetInnerHTML={{ __html: caseStudyFields.conclusionDescription }} />
          </div>
        </div>
      )}
      {form && <ContactForm form={form} />}
    </Main>
  )
}
